import React from 'react';
import { buildClassName } from 'utils/build-class-name';
import ListingStatus from 'components/listing-status';
import formatCurrency from 'utils/format-currency';
import DateText from 'components/date-text';
import styles from './style.module.scss';
import { listingPageModalIds } from 'constants/test-constants';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_ADDRESS_PAGE_PRICE_HISTORY_LISTING } from 'constants/events';
import { dasherize } from '@zoocasa/node-kit/strings/dasherize';

import type Listing from 'data/listing';
import type { IModalContext } from 'contexts/modal';

interface Props {
  currentListing: Listing;
  listing: Listing;
  isPrivate: boolean;
  isAuthenticated: boolean;
  isVisible: boolean;
  isMobile: boolean;
  openModal: IModalContext['openModal'];
  isModal?: boolean;
}

export default function PriceHistoryRow({ currentListing, listing, openModal, isMobile, isVisible, isAuthenticated, isModal = false }: Props) {
  const getSoldPriceLabel = (isCurrentListing: boolean, soldPrice: number | null) => {
    if (isCurrentListing && !soldPrice) {
      if (currentListing.isRental) {
        return 'Not Leased Yet';
      } else if (currentListing.status === 'available') {
        return 'Not Sold Yet';
      } else {
        return '-';
      }
    } else {
      if (currentListing.status === 'available') {
        return 'Not Sold Yet';
      } else {
        return soldPrice ? formatCurrency(soldPrice) : '-';
      }
    }
  };

  const handleContactForPriceClick = () => {
    const prefillData = { requestViewing: false, isShowingAdditionalComments: true, sourceComments: "I'd like to know the price of this property." };

    if (isMobile) {
      openModal('connection-request', { listing: currentListing, isViewingRequest: true, customButtonText: 'Request Info', prefillData: prefillData });
    } else {
      openModal('connection-request-popup', { listing: currentListing, isViewingRequest: true, customButtonText: 'Request Info', prefillData: prefillData });
    }
  };

  const soldDate = currentListing.soldAt || currentListing.expiredAt;
  const isCurrentListing = currentListing.id === listing.id;
  const isNotCurrentlyForSale = currentListing.status !== 'available';
  const externalUrl = isNotCurrentlyForSale ? currentListing.path : currentListing.addressPathWithFallback;
  const listPrice = currentListing.price === 0 ? (
    <span className={styles['contact-for-price']} onClick={handleContactForPriceClick}>
      Contact for Price
    </span>
  ) : (
    currentListing.price !== null ? formatCurrency(currentListing.price) : '-'
  );
  const soldPrice = getSoldPriceLabel(isCurrentListing, currentListing.soldPrice);
  const clickViewListing = () => { 
    if (!isModal) {
      // Only track the GTM event on address pages
      trackEvent(GTM_CLICK_ADDRESS_PAGE_PRICE_HISTORY_LISTING);
    }
    openModal('listing', { listingId: currentListing.id, isHistorical: isNotCurrentlyForSale, externalUrl, isFromPriceHistory: true });
  };
  const shouldBlur = !isVisible || (!isAuthenticated && currentListing.isVow) || (!isAuthenticated && currentListing.isTrebIdxInactive);

  if (isMobile) {
    return (
      <div className={`${styles.row} ${isCurrentListing && styles.active}`} data-testid={ isCurrentListing ? listingPageModalIds.currentPriceHistoryRow : listingPageModalIds.priceHistoryRow }>
        <span className={buildClassName(styles.node, styles[dasherize(currentListing.statusLabel)])}/>
        <div className={styles['mobile-row']}>
          <section className={styles.stat}>
            <span className={`${styles.value} ${styles.large} ${shouldBlur && styles.blur}`}>
              {shouldBlur ? '00/00/0000' : soldDate ? <DateText value={soldDate.toISOString()} format="MMM D, YYYY" render={formattedDate => <span>{formattedDate}</span>} /> : '-'}
            </span>
            <span className={styles.label}>End Date </span>
          </section>
          <section className={buildClassName(styles.stat, styles['center-grid-item'])}>
            <span className={`${styles.value} ${styles.large} ${shouldBlur && styles.blur}`}>{shouldBlur ? '$XXX,XXX' :soldPrice}</span>
            <span className={styles.label}>Sold Price </span>
          </section>
          <section className={buildClassName(styles.stat, styles['center-grid-item'])}>
            <span className={`${styles.value} ${shouldBlur && styles.blur}`}>{!shouldBlur ? <ListingStatus listing={currentListing} className={styles['status-label']} /> : 'Top Secret'}</span>
          </section>
          <section className={styles.stat}>
            <span className={`${styles.value} ${shouldBlur && styles.blur}`}>
              {shouldBlur ? '00/00/0000' : <DateText value={currentListing.addedAt.toISOString()} format="MMM D, YYYY" render={formattedDate => <span>{formattedDate}</span>} />}
            </span>
            <span className={styles.label}>List Date </span>
          </section>
          <section className={buildClassName(styles.stat, styles['center-grid-item'])}>
            <span className={`${styles.value} ${shouldBlur && styles.blur}`} data-testid={listingPageModalIds.priceHistoryRowPrice}>{shouldBlur ? '$XXX,XXX' : listPrice}</span>
            <span className={styles.label}>List Price </span>
          </section>
          <section className={buildClassName(styles.stat, styles['center-grid-item'])}>
            {!isCurrentListing &&<button onClick={clickViewListing}>View</button>}
          </section>
        </div>
      </div>
    );
  } else {
    return (
      <div className={`${styles.grid} ${styles.row} ${isCurrentListing && styles.active}`} data-testid={ isCurrentListing && listingPageModalIds.currentPriceHistoryRow }>
        <span className={buildClassName(styles.node, styles[dasherize(currentListing.statusLabel)])}/>
        <DateText value={currentListing.addedAt.toISOString()} format="MMM D, YYYY" render={formattedDate => <div className={`${shouldBlur && styles.blur}` } data-testid={listingPageModalIds.priceHistoryRowDate }>{shouldBlur ? '00/00/0000' : formattedDate}</div>} />
        <div className={`${shouldBlur && styles.blur}`} data-testid={listingPageModalIds.priceHistoryRowListPrice}>{shouldBlur ? '$XXX,XXX' : listPrice}</div>
        <div className={`${shouldBlur && styles.blur}`} data-testid={listingPageModalIds.priceHistoryRowListStatus}>{!shouldBlur ? <ListingStatus listing={currentListing} className={styles['status-label']} /> : 'Top Secret'}</div>
        <DateText value={soldDate?.toISOString() || ''} format="MMM D, YYYY" render={formattedDate => <div className={`${shouldBlur && styles.blur}`} data-testid={listingPageModalIds.priceHistoryRowSoldDate}>{shouldBlur ? '00/00/0000' : soldDate ? formattedDate : '-'}</div>} />
        <div className={`${shouldBlur && styles.blur}`} data-testid={listingPageModalIds.priceHistoryRowSoldPrice}>{shouldBlur ? '$XXX,XXX' : soldPrice}</div>
        <div>
          {!isCurrentListing && <button onClick={clickViewListing}>View</button>}
        </div>
      </div>
    );
  }
}
